import clsx from "clsx";

import {
  getStoreTime,
  isServiceTypeAvailable,
  isStoreOpen,
} from "~/helpers/siteHelpers";
import DistanceIcon from "~/icons/DistanceIcon";

import styles from "./SiteItem.module.css";
import { SiteWithPosition } from "../SitesList/SitesList";
import { uploadcareConfig } from "~/configs/uploadcareConfig";
import InfoIcon from "~/icons/InfoIcon";
import Button from "~/components/Button/Button";
import { CSSProperties, useContext, useMemo } from "react";
import ThemeContext from "~/contexts/ThemeContext";
import {
  getButtonStyles,
  StoreCartCityFontSize,
  StoreCartNameFontSize,
  StoreCartOtherFontSize,
} from "~/theme/models/themeModels";
import { Shape } from "~/entities/templateEntities";

type SiteItemProps = {
  site: SiteWithPosition;
  withMap: boolean;
  distance: string | null;
  setHoveredId: (id: string | null) => void;
  onVisibleSiteInfo: VoidFunction;
  onSelectSite: VoidFunction;
  isPrevSite?: boolean;
};

const SiteItem: React.FC<SiteItemProps> = ({
  site,
  withMap,
  distance,
  setHoveredId,
  onVisibleSiteInfo,
  onSelectSite,
  isPrevSite,
}) => {
  const { template } = useContext(ThemeContext);

  // Styles
  const cardBackgroundColour = template.storesScreen.card.backgroundColour;
  const cardFontColour = template.storesScreen.card.fontColour;
  const cardNameFontSize =
    StoreCartNameFontSize[template.storesScreen.card.fontSize];
  const cardCityFontSize =
    StoreCartCityFontSize[template.storesScreen.card.fontSize];
  const cardOtherFontSize =
    StoreCartOtherFontSize[template.storesScreen.card.fontSize];
  const buttonStyles: CSSProperties | undefined = useMemo(() => {
    const baseStyles: CSSProperties = getButtonStyles(
      template.storesScreen.card.buttonColour,
      template.storesScreen.card.buttonColour,
      template.storesScreen.card.buttonFontColour,
      template.global.button.buttonSize,
      template.storesScreen.card.buttonStyle
    );
    const hoverStyles: Record<string, string> = {
      "--hover-button-colour": template.storesScreen.card.buttonFontColour,
      "--hover-button-border-colour":
        template.storesScreen.card.buttonFontColour,
      "--hover-button-font-colour": template.storesScreen.card.buttonColour,
    };

    if (template.storesScreen.card.buttonStyle === Shape.FullyRoundedBox) {
      baseStyles.borderRadius = 40;
    }

    return {
      ...baseStyles,
      ...hoverStyles,
    };
  }, [template]);

  const isAtLeastOneServiceAvailable = useMemo(() => {
    return site.orderTypes.some((ot) => isServiceTypeAvailable(site, ot.code));
  }, [site]);

  return (
    <div
      key={site.id}
      className={clsx(styles.site, isStoreOpen(site) && styles.siteActive)}
      style={{
        backgroundColor: cardBackgroundColour,
        color: cardFontColour,
      }}
      onMouseOver={() => setHoveredId(site.id)}
      onMouseOut={() => setHoveredId(null)}
      onFocus={() => setHoveredId(site.id)}
      onBlur={() => setHoveredId(null)}
    >
      <div className={styles.imageWrapper}>
        {site.branding?.onlineOrdering.sitePickerLogo ? (
          <img
            className={clsx(
              styles.image,
              !isStoreOpen(site) && styles.closeStoreImage
            )}
            src={uploadcareConfig.getUrl(
              site.branding?.onlineOrdering.sitePickerLogo.fileId,
              site.branding?.onlineOrdering.sitePickerLogo.modifiers
            )}
            alt={site.branding?.onlineOrdering.sitePickerLogo.fileName}
          />
        ) : (
          <div className={styles.emptyImage}>
            <span>{site.name}</span>
          </div>
        )}
      </div>
      <div
        className={styles.siteInfo}
        style={{
          fontSize: cardOtherFontSize,
        }}
      >
        <div
          className={styles.siteName}
          style={{
            fontSize: cardNameFontSize,
          }}
        >
          {site.name}
        </div>
        <div
          className={styles.siteCity}
          style={{
            fontSize: cardCityFontSize,
          }}
        >
          {site.address.city.trim() || "City not specified"}
        </div>
        <div className={styles.siteAvailable}>{`${getStoreTime(site)}`}</div>
        <div className={styles.siteMoreInfo} onClick={onVisibleSiteInfo}>
          <InfoIcon />
          <span>More information</span>
        </div>
        <div
          className={clsx(
            styles.siteActions,
            !withMap && styles.siteActionsRight
          )}
        >
          {site.serviceSettings.length > 0 && (
            <Button onClick={onSelectSite} style={buttonStyles}>
              {isStoreOpen(site) && isAtLeastOneServiceAvailable
                ? "Order Now"
                : "View Menu"}
            </Button>
          )}
          {!!distance && withMap && (
            <div className={styles.siteDistance}>
              <DistanceIcon />
              <span>{distance}</span>
            </div>
          )}
        </div>
        {isPrevSite && <div className={styles.prevSite}>Previous</div>}
      </div>
    </div>
  );
};

export default SiteItem;
